import { Icon } from "@/components/ui/Icon/Icon";
import styles from "./PasswordStrength.module.css";

/**
 * Scores a password against the same rules the Cognito User Pool
 * enforces, plus a length-based "stretch" bonus so the meter reaches
 * the highest tier for very long passwords. Keep this in sync with
 * infra/template.yaml PasswordPolicy.
 */
export function scorePassword(pw: string): {
  score: 0 | 1 | 2 | 3 | 4;
  meetsLength: boolean;
  meetsCase: boolean;
  meetsNumber: boolean;
} {
  const meetsLength = pw.length >= 12;
  const meetsCase = /[a-z]/.test(pw) && /[A-Z]/.test(pw);
  const meetsNumber = /\d/.test(pw);
  const rules = [meetsLength, meetsCase, meetsNumber].filter(Boolean).length;
  const longBonus = pw.length >= 16 ? 1 : 0;
  const score = Math.max(
    0,
    Math.min(4, (rules === 3 ? 3 : rules) + longBonus),
  ) as 0 | 1 | 2 | 3 | 4;
  return { score, meetsLength, meetsCase, meetsNumber };
}

const LABELS = ["Too short", "Weak", "Okay", "Strong", "Excellent"] as const;

interface PasswordStrengthBarProps {
  password: string;
}

export function PasswordStrengthBar({ password }: PasswordStrengthBarProps) {
  const { score } = scorePassword(password);
  return (
    <div className={styles.bar} aria-hidden="true">
      <div className={styles.cells}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`${styles.cell} ${i < score ? styles[`fill_${score}`] : ""}`}
          />
        ))}
      </div>
      <span className={`${styles.label} ${styles[`fill_${score}`]}`}>{LABELS[score]}</span>
    </div>
  );
}

interface PasswordRuleProps {
  met: boolean;
  children: React.ReactNode;
}

export function PasswordRule({ met, children }: PasswordRuleProps) {
  return (
    <div className={`${styles.rule} ${met ? styles.ruleMet : ""}`}>
      <span className={styles.ruleDot} aria-hidden="true">
        <Icon name={met ? "check" : "minus"} size={9} strokeWidth={3} />
      </span>
      <span className={styles.ruleLabel}>{children}</span>
    </div>
  );
}
